import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';

const DURATION_MS = 5000;

interface ToastProps {
  message: string;
  onClose: () => void;
}

/** A success confirmation in the top-right corner that goes away on its own; the bar is the time left. */
export default function Toast({ message, onClose }: ToastProps) {
  // The timer starts once: a parent re-render with a new onClose must not restart it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => onCloseRef.current(), DURATION_MS);
    const frame = requestAnimationFrame(() => setRunning(true));
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      role="status"
      className="fixed right-6 top-[76px] z-[60] w-[300px] overflow-hidden rounded-md border border-border-subtle bg-surface shadow-lg"
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 fill-green-500 text-white" aria-hidden="true" />
        <p className="flex-1 text-sm text-content">{message}</p>
        <button type="button" aria-label="Dismiss" onClick={onClose} className="text-content-muted">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        className="h-1 bg-green-500 transition-[width] ease-linear"
        style={{ width: running ? '0%' : '100%', transitionDuration: `${DURATION_MS}ms` }}
      />
    </div>
  );
}
